import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import resourcesToBackend from 'i18next-resources-to-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

// Bundle ONLY the fallback language (en) statically so the very first paint has
// it synchronously (no flash of raw keys). All OTHER languages are loaded lazily
// via dynamic import() below -> Vite code-splits each locale into its own chunk.
import enTranslation from '@/locales/en/translation.json';

// ──────────────────────────────────────────────────────────────────────────
// API-DRIVEN TRANSLATIONS — DISABLED (kept here so it can be re-connected).
//
// Translations now come from LOCAL files under src/locales/<lng>/translation.json
// (lazy-loaded per language). The old remote http-backend wiring is preserved
// below, commented out. TO RE-ENABLE THE API:
//   1) re-install the backend:  npm install i18next-http-backend
//   2) uncomment the `import Backend ...` line and the `.use(Backend)` call
//   3) uncomment the `backend: { loadPath ... }` block in .init()
//   4) remove (or keep alongside) the `.use(resourcesToBackend(...))` line and
//      the bundled `resources` / `partialBundledLanguages` options below
// learn more: https://github.com/i18next/i18next-http-backend
//
// import Backend from 'i18next-http-backend';
// ──────────────────────────────────────────────────────────────────────────
// don't want to use this?
// have a look at the Quick start guide
// for passing in lng and translations on init

const testRegex = /^[0-9]$/;
const debugMissingKeys = window.location.hostname === 'winner.test.wlscasino.com';
const exists: any = {};

const fallbackLng = window.config.defaultLanguage || 'en';

// eslint-disable-next-line
i18n
  // OLD (API): load translation over http from the templatization backend.
  // .use(Backend)

  // NEW (LOCAL, LAZY): dynamic-import backend. The fallback language is bundled
  // via `resources` below; every other language resolves to its own async chunk.
  .use(
    // Relative path (not the `@` alias): Vite only code-splits dynamic imports
    // whose specifier starts with ./ or ../ — an alias breaks the glob analysis.
    resourcesToBackend(
      (language: string, namespace: string) => import(`../locales/${language}/${namespace}.json`),
    ),
  )

  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)

  // pass the i18n instance to react-i18next.
  .use(initReactI18next)

  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    supportedLngs: window.config.languages,
    fallbackLng,

    // Make the fallback language available at init -> first paint never shows
    // raw keys for default-language users. Other languages still lazy-load via
    // the resourcesToBackend dynamic import above.
    resources: {
      // `enTranslation` IS the English file, so it must register under the literal
      // `en` key — never a (possibly non-en) `fallbackLng`. Keying it by `fallbackLng`
      // would, for a tenant whose `defaultLanguage` is e.g. `mx`, register English copy
      // under `mx` and shadow that locale's real lazy-loaded chunk. (English source text
      // is the key, so a non-en default still paints readable English before its chunk
      // arrives.)
      en: { translation: enTranslation },
    },
    // Allow the statically-bundled `resources` (en) and the backend-loaded
    // languages (mx, ...) to coexist; without this, providing `resources` can
    // short-circuit the backend so other languages never load.
    partialBundledLanguages: true,
    // No explicit `lng`: an explicit value bypasses LanguageDetector, so the user's
    // pick wouldn't survive a refresh. Letting the detector run restores it from the
    // cache (querystring `?lng=` → localStorage/cookie → navigator). `changeLanguage`
    // writes that cache, so the selected language now persists across reloads.
    detection: {
      order: ['querystring', 'localStorage', 'cookie', 'navigator'],
      lookupQuerystring: 'lng',
      caches: ['localStorage', 'cookie'],
    },

    debug: false, //import.meta.env.DEV, // debug only on development mode
    nonExplicitSupportedLngs: false,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },

    nsSeparator: false,
    keySeparator: false,

    initImmediate: false, // setting initImediate to false, will load the resources synchronously

    // OLD (API): remote loadPath for the http-backend. Preserved for re-connect
    // (see the header note: re-install i18next-http-backend + uncomment .use(Backend)).
    // backend: {
    //   loadPath: function (lng: string, _ns: string) {
    //     return `${window.config.translationApiUrl}/api/multilang/v1/translate/table/${window.config.translationProjectCode}/${lng}?tenant_id=${window.config.clientId}`;
    //   },
    // },

    react: {
      useSuspense: false,
    },

    saveMissing: false, // debugMissingKeys,
    missingKeyHandler: (_lngs, _ns, key, fallbackValue) => {
      if (debugMissingKeys && key && !testRegex.exec(key[0])) {
        if (!exists[key]) {
          exists[key] = true;
          console.log('missingKeyHandler', { key, fallbackValue });
        }
      }
    },
  });

// Keep the legacy `language` localStorage key in lockstep with i18next's own
// authoritative `i18nextLng` cache (written by detection.caches). i18next persists the
// active language to `i18nextLng` on every change; this mirrors that SAME value to
// `language` — via i18next's `languageChanged` event, the one chokepoint every switch
// flows through (reducer, UI switcher, detector) — and once now for the initial detected
// value, so the two can never drift. Needed because some paths write only `i18nextLng`
// (a `?lng=` / startProperties pre-seed in dev.js), which used to leave `language` stale.
// `i18nextLng` stays the single source of truth; `language` is a derived shadow written
// ONLY here (the `changeLanguage` reducer no longer writes it).
const mirrorLanguageKey = (lng: string): void => {
  try {
    if (lng) localStorage.setItem('language', lng);
  } catch (e) {
    /* localStorage unavailable (private mode / quota) — i18nextLng remains authoritative */
  }
};
mirrorLanguageKey(i18n.language);
i18n.on('languageChanged', mirrorLanguageKey);

export const getLanguage = () => {
  return i18n.language ? i18n.language : 'en';
};

export default i18n;
